import { YHistoryEditor, YjsEditor } from '@tunasong/sync-lib'

/**
 * Runs `edit` as an undo step of its own: one undo takes it out whole, and nothing else.
 *
 * A collaborative document's undo is a Y.js UndoManager (collab-yjs), which merges changes less than 500 ms apart
 * into one step. A command that inserts a block straight after the user typed would otherwise join the typing's
 * step, and its undo would take the typing too (melody toolkit M1, "one insert, one undo"; #2660).
 *
 * slate-yjs sends local edits to the Y.Doc on `onChange`, so the edits on either side are flushed before capturing
 * stops: what came before closes its step, and `edit` is closed before anything the user does next. An editor
 * without Y.js history runs `edit` as it is: slate-history does not merge an inserted node with the typing before it.
 */
export const asOwnUndoStep = (editor: object, edit: () => void) => {
  if (!YHistoryEditor.isYHistoryEditor(editor)) {
    edit()
    return
  }
  YjsEditor.flushLocalChanges(editor)
  editor.undoManager.stopCapturing()
  edit()
  YjsEditor.flushLocalChanges(editor)
  editor.undoManager.stopCapturing()
}

/**
 * Runs `edit` outside the undo history: it reaches the Y.Doc, and so every collaborator, but no undo step takes it
 * out on its own. For an edit that completes an earlier step, like an AI result filling in its placeholder: undoing
 * the placeholder's insert then takes out the block with its result, and no undo brings the placeholder back
 * (GH #2922).
 *
 * The edit runs under withYHistory's `withoutSavingOrigin`, a local origin the UndoManager does not track. Pending
 * edits are flushed first, so typing before it keeps its own origin. An editor without Y.js history runs `edit` as it is.
 */
export const outsideUndoHistory = (editor: object, edit: () => void) => {
  if (!YHistoryEditor.isYHistoryEditor(editor)) {
    edit()
    return
  }
  YjsEditor.flushLocalChanges(editor)
  YHistoryEditor.withoutSaving(editor, edit)
  YjsEditor.flushLocalChanges(editor)
}
